const EMPTY_SCORE = "-:-";

const isPresentScoreValue = (value) =>
  value !== null && value !== undefined && value !== "";

const normalizeScoreString = (score) => String(score ?? "").trim();

const splitScore = (score) => {
  const compactScore = normalizeScoreString(score).replace(/\s+/g, "");
  const separator = compactScore.includes(":")
    ? ":"
    : compactScore.includes("-")
      ? "-"
      : null;

  if (!separator) {
    return null;
  }

  const [homeScore, awayScore] = compactScore.split(separator);
  if (!homeScore || !awayScore) {
    return null;
  }

  return [homeScore, awayScore];
};

export const hasMatchScoreData = (match) =>
  isPresentScoreValue(match?.home_team_score) &&
  isPresentScoreValue(match?.away_team_score);

// A match counts as "played" only when it carries recorded stat data. Some
// endpoints (e.g. team matches_opt) default the score to 0:0 for matches that
// were never tracked, which is indistinguishable from a real 0-0 draw by score
// alone. A genuinely played match — even a 0-0 — has non-zero stats (passes,
// on-ball events, ...), so we use that as the signal for "data available".
export const hasMatchStats = (match) =>
  Array.isArray(match?.parameters) &&
  match.parameters.some(
    (parameter) => Number(parameter?.value ?? parameter?.count ?? 0) > 0
  );

export const formatMatchScore = (match, { separator = ":" } = {}) => {
  if (hasMatchScoreData(match)) {
    return `${match.home_team_score}${separator}${match.away_team_score}`;
  }

  const scoreParts = splitScore(match?.score);
  if (!scoreParts) {
    return EMPTY_SCORE;
  }

  const [homeScore, awayScore] = scoreParts;
  if (homeScore === "0" && awayScore === "0") {
    return EMPTY_SCORE;
  }

  return `${homeScore}${separator}${awayScore}`;
};

export const getMatchScoreParts = (match) => {
  if (hasMatchScoreData(match)) {
    return [match.home_team_score, match.away_team_score];
  }

  const scoreParts = splitScore(match?.score);
  if (!scoreParts) {
    return ["-", "-"];
  }

  const [homeScore, awayScore] = scoreParts;
  if (homeScore === "0" && awayScore === "0") {
    return ["-", "-"];
  }

  return scoreParts;
};
